import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "./MainLayout";
import apiClient from "../services/axiosInterceptor";
import { toast } from "react-toastify";
import { Loader, Tag, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateMaxUploadFileSize, updatePlanUploadLimit, updateSubscription } from "../store/slices/authSlice";
import { initiatePayment } from "../services/paymentService";
import { Modal, Input, Button, Divider } from "antd";

const PricingCard = ({
  plan,
  price,
  subPrice,
  features,
  note,
  isFeatured = false,
  onBuy, // 2. Receive the onBuy handler
  isLoading = false,
  disabled = false,
}) => {
  const cardClasses = `p-6 rounded-2xl border ${
    isFeatured
      ? "border-purple-500 bg-purple-500/10"
      : "border-white/20 bg-gray-900/50"
  }`;

  const buttonClasses = `w-full py-3 rounded-lg font-semibold transition ${
    isFeatured
      ? "bg-purple-500 hover:bg-purple-600 text-white"
      : "bg-gray-700 hover:bg-gray-600 text-white"
  }`;

  return (
    <div className={cardClasses}>
      <h3 className="text-2xl font-bold text-white">{plan}</h3>

      <p className="mt-4 text-2xl font-extrabold text-white">{price}</p>

      {price !== "Contact sales" && (
        <p className="text-xs text-gray-400 mt-1">
          (All prices are exclusive of taxes)
        </p>
      )}

      {subPrice && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="line-through text-gray-400">
            {subPrice.original}
          </span>
          <span className="rounded-full bg-green-500/20 px-2 py-1 text-xs font-semibold text-green-400">
            {subPrice.discount}
          </span>
        </div>
      )}
      <button
        className={`${buttonClasses} mt-10 flex items-center justify-center gap-2 ${isLoading || disabled ? "opacity-70 cursor-not-allowed" : ""}`}
        onClick={() => onBuy && onBuy(plan, price)}
        disabled={isLoading || disabled} // 3. Trigger payment on click
      >
        {isLoading && <Loader className="animate-spin w-5 h-5" />}
        {isLoading
          ? "Processing..."
          : price === "Contact sales"
            ? "Contact Sales"
            : "Get Started"}
      </button>

      <ul className="mt-8 space-y-4 text-gray-300 text-left">
        {features.map((feature, index) => (
          <li key={index} className="flex gap-3">
            <span className="text-green-400">✔</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {note && <p className="mt-4 text-xs text-gray-400">{note}</p>}
    </div>
  );
};

const Pricing = ({ isInDashboard = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [billingCycle, setBillingCycle] = useState("monthly");
  
  // Modal & Preview States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const email = useSelector((state) => state.auth.email);

  const fetchPricingPreview = async (plan, cycle, coupon) => {
    setPreviewLoading(true);
    try {
      const response = await apiClient.post("/payments/pricing-preview", {
        plan_id: plan[cycle].plan_id,
        billing_cycle: cycle,
        coupon_code: coupon,
      });
      setPreviewData(response.data);
      if (coupon && response.data.coupon_applied) {
        toast.success("Coupon applied successfully!",{theme: "colored"});
      } else if (coupon && !response.data.coupon_applied) {
        toast.error(response.data.message || "Invalid coupon code",{theme: "colored"});
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch pricing details.",{theme: "colored"});
    } finally {
      setPreviewLoading(false);
    }
  };

  // 4. Plan Selection Handler
  const handlePlanClick = (planName, priceString) => {
    const plan = pricingPlans.find((p) => p.plan === planName);

    if (!plan) {
      toast.error("Invalid plan selected",{theme: "colored"});
      return;
    }

    // Contact Sales Handling
    if (priceString === "Contact sales") {
      navigate(isInDashboard ? "/dashboard/contact-us" : "/contact-us");
      return;
    }

    if (!isInDashboard) {
      navigate("/login");
      return;
    }

    // Open Modal and Fetch Preview
    setSelectedPlan(plan);
    setCouponCode("");
    setPreviewData(null);
    setIsModalOpen(true);
    fetchPricingPreview(plan, billingCycle, "");
  };

  const handleApplyCoupon = () => {
    if (!selectedPlan) return;
    fetchPricingPreview(selectedPlan, billingCycle, couponCode);
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    if (selectedPlan) {
      fetchPricingPreview(selectedPlan, billingCycle, "");
    }
  };

  const handleProceedToPay = () => {
    if (!selectedPlan) return;

    initiatePayment({
      plan_name: selectedPlan.plan,
      plan_id: selectedPlan[billingCycle].plan_id,
      user,
      email,
      billing_cycle: billingCycle,
      coupon_code: previewData?.coupon_applied ? couponCode : "", // Pass coupon code to payment service
      setLoading: setPaymentLoading,

      onSuccess: async (verifyData) => {
        try {
          const refreshResponse = await apiClient.post(
            `/payments/refresh-token`,
          );

          sessionStorage.setItem(
            "refresh_token",
            refreshResponse.data.access_token,
          );

          dispatch(updateSubscription(refreshResponse.data.plan_name));
          dispatch(updateMaxUploadFileSize(refreshResponse.data.max_upload_file_size_limit_mb));
          dispatch(updatePlanUploadLimit(refreshResponse.data.plan_upload_limit));

          toast.success(verifyData.message || "Payment Successful 🎉",{theme: "colored"});
          setIsModalOpen(false);
          navigate("/dashboard");
        } catch (err) {
          toast.error("Subscription update failed.",{theme: "colored"});
        }
      },

      onFailure: async () => {
        toast.error("Payment verification failed.",{theme: "colored"});
      },
    });
  };

  const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount / 100);


  const pricingPlans = [
    {
      plan: "Free",
      plan_id: 1,
      monthly: {
        plan_id: 1,
        price: "₹0 / month",
        subPrice: { original: "₹999", discount: "Save 100%" },
      },
      yearly: {
        plan_id: 1,
        price: "₹0 / yr",
        subPrice: { original: "₹999", discount: "Save 100%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Ready dashboards",
        "2 files upload monthly",
        "5 prompts monthly",
        "File size up to 5 MB",
        "Download your work",
      ],
    },
    {
      plan: "Basic Individual",
      plan_id: 2,
      monthly: {
        plan_id: 2,
        price: "₹999 / month",
        subPrice: { original: "₹1,999", discount: "Save 50%" },
      },
      yearly: {
        plan_id: 7, // need to change a/c to live database
        price: "₹11,994 / yr",
        subPrice: { original: "₹23,999", discount: "Save 50%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Ready dashboards",
        "5 files upload daily",
        "15 prompts monthly",
        "File size up to 25 MB",
        "Download your work",
        "Save your work – 7 days",
      ],
    },
    {
      plan: "Pro Power Users",
      plan_id: 3,
      isFeatured: true,
      monthly: {
        plan_id: 3,
        price: "₹2,499 / month",
        subPrice: { original: "₹4,999", discount: "Save 50%" },
      },
      yearly: {
        plan_id: 8,
        price: "₹29,999 / yr",
        subPrice: { original: "₹59,999", discount: "Save 50%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Advanced reasoning",
        "Ready dashboards",
        "10 files upload daily",
        "File size up to 25 MB",
        "Download your work",
        "Save your work – 30 days",
        "30 prompts daily",
        "900 prompts monthly",
      ],
    },
    {
      plan: "Business Team",
      plan_id: 4,
      isFeatured: false,
      monthly: {
        plan_id: 4,
        price: "₹14,999 / month",
        subPrice: { original: "₹29,999", discount: "Save 50%" },
      },
      yearly: {
        plan_id: 9,
        price: "₹1,79,999 / yr",
        subPrice: { original: "₹3,59,999", discount: "Save 50%" },
      },
      features: [
        "Unlimited access to all models",
        "Unlimited insights",
        "Advanced reasoning",
        "Unlimited file uploads",
        "File size up to 50 MB",
        "Save & download work",
        "Priority email support",
        "Permanent file storage",
        "180 prompts daily",
        "5,400 prompts monthly",
      ],
    },
    {
      plan: "ENTERPRISE",
      monthly: { plan_id: 5, price: "Contact sales" },
      yearly: { plan_id: 5, price: "Contact sales" },
      features: [
        "Custom usage limits",
        "Dedicated support",
        "Custom storage & SLA",
        "Security & compliance",
      ],
      note: "For large teams & custom requirements",
    },
  ];

  const content = (
    <main
      className={`w-full max-w-7xl mx-auto flex flex-col items-center text-center px-6 md:px-12 space-y-12 pb-24 ${isInDashboard ? "pt-10" : "pt-32"}`}
    >
      <div className="space-y-4">
        <h1 className="text-5xl md:text-7xl font-extrabold text-gray-200 leading-tight">
          Pricing Plans
        </h1>
        <p className="text-xl text-gray-400">
          Choose the plan that's right for your team.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center gap-2 rounded-full bg-gray-800 p-1">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
            billingCycle === "monthly"
              ? "bg-purple-500 text-white"
              : "text-gray-400"
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle("yearly")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
            billingCycle === "yearly"
              ? "bg-purple-500 text-white"
              : "text-gray-400"
          }`}
        >
          Yearly
        </button>
      </div>

      {/* Pricing Cards */}
      <div
        className={`grid grid-cols-1 ${isInDashboard ? "md:grid-cols-3" : "md:grid-cols-4"} gap-6 w-full max-w-7xl pt-8`}
      >
        {pricingPlans
          .filter((plan) => !(isInDashboard && plan.plan === "Free"))
          .map((plan, index) => {
            const billing = plan[billingCycle];

            return (
              <PricingCard
                key={index}
                plan={plan.plan}
                price={billing.price}
                subPrice={billing.subPrice}
                features={plan.features}
                note={plan.note}
                isFeatured={plan.isFeatured}
                onBuy={handlePlanClick} // 5. Pass the handler here
                isLoading={false}
              />
            );
          })}
      </div>

      {/* Order Summary Modal */}
      <Modal
        title={<div className="text-xl font-bold text-white">Order Summary</div>}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        width={500}
        closeIcon={<X className="text-white" />}
        styles={{
          content: {
            backgroundColor: "#1f2937", // gray-800
            border: "1px solid #374151", // gray-700
          },
          header: {
            backgroundColor: "#1f2937", // gray-800
            color: "white",
          },
          mask: {
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <div className="space-y-6 py-4">
          {/* Plan Details */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {selectedPlan?.plan}
              </h3>
              <p className="text-sm text-gray-400 capitalize">
                {billingCycle} Billing
              </p>
            </div>
            <div className="text-right">
              {previewData ? (
                <span className="text-xl font-bold text-purple-400">
                  {formatCurrency(previewData.base_price)}
                </span>
              ) : (
                <div className="h-6 w-20 bg-gray-700 animate-pulse rounded"></div>
              )}
            </div>
          </div>

          <Divider className="my-2" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

          {/* Breakdown */}
          <div className="space-y-3 text-sm text-gray-300">
            <div className="flex justify-between text-green-400">
              <span>Discount</span>
              {previewData ? (
                <span>- {formatCurrency(previewData.discount_amount)}</span>
              ) : (
                <div className="h-4 w-12 bg-gray-700 animate-pulse rounded"></div>
              )}
            </div>
            <div className="flex justify-between">
              <span>Subtotal</span>
              {previewData ? (
                <span>{formatCurrency(previewData.subtotal)}</span>
              ) : (
                <div className="h-4 w-12 bg-gray-700 animate-pulse rounded"></div>
              )}
            </div>

            <div className="flex justify-between">
              <span>Tax (GST 18%)</span>
              {previewData ? (
                <span>{formatCurrency(previewData.tax_amount)}</span>
              ) : (
                <div className="h-4 w-12 bg-gray-700 animate-pulse rounded"></div>
              )}
            </div>

            <Divider className="my-2" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

            <div className="flex justify-between text-lg font-bold text-white">
              <span>Total</span>
              {previewData ? (
                <span>{formatCurrency(previewData.total_amount)}</span>
              ) : (
                <div className="h-6 w-24 bg-gray-700 animate-pulse rounded"></div>
              )}
            </div>
          </div>

          {/* Coupon Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Tag className="w-4 h-4" /> Have a coupon?
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="uppercase hover:border-purple-500 focus:border-purple-500"
                style={{
                  backgroundColor: "#374151",
                  color: "white",
                  borderColor: "#4b5563",
                }}
                disabled={previewData?.coupon_applied}
                allowClear
                suffix={
                  previewData?.coupon_applied ? (
                    <X
                      className="w-4 h-4 cursor-pointer text-gray-400 hover:text-red-400"
                      onClick={handleRemoveCoupon}
                    />
                  ) : null
                }
              />
              {!previewData?.coupon_applied && (
                <Button
                  onClick={handleApplyCoupon}
                  loading={previewLoading}
                  disabled={!couponCode || paymentLoading}
                  className="text-white border-gray-600 hover:text-purple-400 hover:border-purple-400 bg-transparent disabled:text-gray-600 disabled:border-gray-600 disabled:bg-transparent"
                >
                  Apply
                </Button>
              )}
            </div>
            {previewData?.coupon_applied && (
              <p className="text-xs text-green-400">
                Coupon applied successfully!
              </p>
            )}
          </div>

          {/* Pay Button */}
          <Button
            type="primary"
            size="large"
            block
            onClick={handleProceedToPay}
            loading={paymentLoading}
            disabled={previewLoading || !previewData}
            className="bg-purple-600 hover:bg-purple-700 h-12 text-lg font-semibold border-none"
          >
            Proceed to Pay
          </Button>
        </div>
      </Modal>
    </main>
  );

  return isInDashboard ? content : <MainLayout>{content}</MainLayout>;
};

export default Pricing;